import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Button,
    Card,
    Checkbox,
    ColorPicker,
    Drawer,
    Flex,
    Input,
    Segmented,
    Skeleton,
    Slider,
    Switch,
    Tag,
    Typography,
} from 'antd'
import { CheckOutlined, MessageOutlined } from '@ant-design/icons'
import { useBlocker } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth'
import { UPDATE_SITE } from '../../user/permission'
import { AssistantInput } from '../api'
import { AssistantSettings, ChatSettings, VoiceInput } from '../site'
import {
    useAnalyzeVoice,
    useAssistant,
    useChatSettings,
    useSaveAssistantPage,
    useSite,
    useTryOutAssistant,
} from '../hooks'
import { ChatPreview, ChatPreviewHandle, TestHistory } from './chat-preview'
import { SiteVoice } from './site-voice'

export type SiteAssistantProps = {
    siteId: string
}

type Values = {
    assistantName: string
    welcomeMessage: string
    capabilities: string[]
    enabled: boolean
    credit: boolean | null
    privacyUrl: string | null
    label: string
    position: ChatSettings['position']
    color: string
    textColor: string
    radius: number
    voice: VoiceInput
}

function valuesFrom(assistant: AssistantSettings, chat: ChatSettings): Values {
    return {
        assistantName: assistant.assistantName ?? '',
        welcomeMessage: assistant.welcomeMessage ?? '',
        capabilities: assistant.capabilities
            .filter((capability) => capability.enabled)
            .map((capability) => capability.key),
        enabled: chat.enabled,
        credit: chat.credit,
        privacyUrl: chat.privacyUrl,
        label: chat.label,
        position: chat.position,
        color: chat.color,
        textColor: chat.textColor,
        radius: chat.radius,
        voice: {
            tone: assistant.voice.tone,
            address: assistant.voice.address,
            style: assistant.voice.style,
            emoji: assistant.voice.emoji,
            instructions: assistant.voice.instructions,
            fallback: assistant.voice.fallback,
            suggestions: assistant.voice.suggestions,
        },
    }
}

const ASSISTANT_KEYS = ['assistantName', 'welcomeMessage', 'capabilities'] as const

const CHAT_KEYS = ['enabled', 'credit', 'privacyUrl', 'label', 'position', 'color', 'textColor', 'radius'] as const

/** The chat on a website is 380 pixels wide with 20 pixels around it; the card's border adds 2. */
const PREVIEW_WIDTH = 422

/** Below this, the settings get too cramped next to the preview, and the test chat opens in a panel instead. */
const MIN_FORM_WIDTH = 360

/** Whether two values are the same, also for arrays and objects. */
function sameJson(value: unknown, other: unknown): boolean {
    return JSON.stringify(value) === JSON.stringify(other)
}

function assistantInputFrom(values: Values): AssistantInput {
    return {
        assistantName: values.assistantName.trim() || null,
        welcomeMessage: values.welcomeMessage.trim() || null,
        capabilities: values.capabilities,
    }
}

function chatInputFrom(values: Values, wordpress: boolean): Partial<ChatSettings> {
    return {
        enabled: values.enabled,
        credit: values.credit,
        color: values.color,
        textColor: values.textColor,
        position: values.position,
        radius: values.radius,
        label: values.label,
        // A WordPress website's privacy policy comes from WordPress.
        ...(wordpress ? {} : { privacyUrl: values.privacyUrl }),
    }
}

/** The width of an element, followed while it changes: 0 until the element is measured. */
function useWidth() {
    const [width, setWidth] = useState(0)
    const observer = useRef<ResizeObserver>(null)

    const measure = useCallback((element: HTMLDivElement | null) => {
        observer.current?.disconnect()

        if (element) {
            observer.current = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width))
            observer.current.observe(element)
        }
    }, [])

    return {
        width,
        measure,
    }
}

/**
 * The assistant and the chat on one page: who the assistant is, whether and how the chat shows on the website, and the
 * real widget next to it, which is also the test chat.
 */
export function SiteAssistant({ siteId }: SiteAssistantProps) {
    const { t } = useTranslation()
    const { can } = useAuth()
    const { data: site } = useSite(siteId)

    // While Evaa reads the website's tone, the result is fetched until it is there.
    const [analyzingSince, setAnalyzingSince] = useState<number | null>(null)
    const { data: assistant } = useAssistant(siteId, analyzingSince !== null ? 3000 : false)
    const analyze = useAnalyzeVoice(siteId)

    const { data: chat } = useChatSettings(siteId)
    const save = useSaveAssistantPage(siteId)
    const tryOut = useTryOutAssistant(siteId)
    const preview = useRef<ChatPreviewHandle>(null)
    const [testOpen, setTestOpen] = useState(false)
    const { width, measure } = useWidth()
    const wide = width === 0 || width >= MIN_FORM_WIDTH + 16 + PREVIEW_WIDTH
    const [values, setValues] = useState<Values>()
    const editable = can(UPDATE_SITE)

    const saved = useMemo(() => (assistant && chat ? valuesFrom(assistant, chat) : undefined), [assistant, chat])

    // New data from the server replaces the form, unless the user changed something (for example while the tone is
    // read in the background).
    const previous = useRef(saved)

    useEffect(() => {
        const untouched = !values || sameJson(values, previous.current)

        if (saved && untouched) {
            setValues(saved)
        }

        previous.current = saved
        // Only when the saved data changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saved])

    const startAnalysis = useCallback(() => {
        setAnalyzingSince(Date.now())
        analyze.mutate()
    }, [analyze])

    const analyzedAt = assistant?.voice.profile?.analyzedAt

    // Stops polling when the new result is there, or after a minute (for example when there are no pages yet).
    useEffect(() => {
        if (analyzingSince === null) {
            return
        }

        const isAnalyzed = analyzedAt && new Date(analyzedAt).getTime() >= analyzingSince - 5000

        if (isAnalyzed) {
            setAnalyzingSince(null)

            return
        }

        const timer = window.setTimeout(() => setAnalyzingSince(null), 60000)

        return () => window.clearTimeout(timer)
    }, [analyzingSince, analyzedAt])

    // Websites that were read before Evaa read their tone get it the first time this page opens.
    const asked = useRef(false)

    useEffect(() => {
        const toneUnknown = assistant && assistant.voice.profile === null

        if (toneUnknown && editable && !asked.current) {
            asked.current = true
            startAnalysis()
        }
    }, [assistant, editable, startAnalysis])

    const dirty = Boolean(values && saved && !sameJson(values, saved))

    useBlocker({
        shouldBlockFn: () => dirty && !window.confirm(t('You have unsaved changes. Are you sure you want to leave?')),
        enableBeforeUnload: () => dirty,
    })

    // The test chat answers with the settings on the page, also before they are saved.
    const current = useRef(values)
    current.current = values

    if (!values || !saved || !assistant || !site) {
        return <Skeleton active />
    }

    const wordpress = site.type === 'wordpress'
    const profile = assistant.voice.profile
    const showsPreviewCard = wide || !editable

    const set = (patch: Partial<Values>) => {
        setValues({
            ...values,
            ...patch,
        })
    }

    const submit = () => {
        const changed = (keys: readonly (keyof Values)[]) => keys.some((key) => !sameJson(values[key], saved[key]))

        save.mutate({
            assistant: changed(ASSISTANT_KEYS) ? assistantInputFrom(values) : undefined,
            chat: changed(CHAT_KEYS) ? chatInputFrom(values, wordpress) : undefined,
            voice: changed(['voice']) ? values.voice : undefined,
        })
    }

    const answer = async (content: string, messages: TestHistory) => {
        const settings = current.current ?? values

        // The API takes the last 20 messages of at most 2,000 characters.
        const recentMessages = messages.slice(-20).map((message) => ({
            ...message,
            content: message.content.slice(0, 2000),
        }))

        const result = await tryOut.mutateAsync({
            content,
            messages: recentMessages,
            assistantName: settings.assistantName.trim() || null,
            capabilities: settings.capabilities,
            voice: settings.voice,
        })

        return {
            message: result.message,
            cards: result.cards,
        }
    }

    const testChat = (
        <TestChat
            previewRef={preview}
            values={values}
            assistant={assistant}
            editable={editable}
            onQuestion={editable ? answer : undefined}
        />
    )

    const restart = editable && (
        <Button
            size="small"
            onClick={() => preview.current?.reset()}
        >
            {t('Start again')}
        </Button>
    )

    return (
        <div
            ref={measure}
            className={wide ? 'flex items-start gap-4' : 'flex flex-col gap-6'}
        >
            <div className="min-w-0 flex-1">
                <Flex
                    vertical
                    gap="large"
                >
                    <AssistantCard
                        values={values}
                        onChange={set}
                        editable={editable}
                        assistant={assistant}
                    />

                    <SiteVoice
                        value={values.voice}
                        onChange={(voice) => set({ voice })}
                        profile={profile}
                        editable={editable}
                        analyzing={analyzingSince !== null}
                        onAnalyze={startAnalysis}
                    />

                    <ChatOnWebsiteCard
                        values={values}
                        onChange={set}
                        editable={editable}
                        wordpress={wordpress}
                        hasVacancies={(site?.openVacanciesCount ?? 0) > 0}
                    />

                    <AppearanceCard
                        values={values}
                        onChange={set}
                        editable={editable}
                        wordpress={wordpress}
                    />

                    {editable && (
                        <SaveBar
                            withTestButton={!wide}
                            dirty={dirty}
                            saving={save.isPending}
                            onTest={() => setTestOpen(true)}
                            onCancel={() => setValues(saved)}
                            onSave={submit}
                        />
                    )}
                </Flex>
            </div>
            {showsPreviewCard ? (
                // The real size of the chat on a website, so text wraps the same as there.
                <Card
                    title={editable ? t('Test chat') : t('Preview')}
                    extra={restart}
                    className={wide ? 'sticky top-4 shrink-0 overflow-hidden' : 'overflow-hidden'}
                    style={{ width: wide ? PREVIEW_WIDTH : undefined }}
                    styles={{ body: { padding: 0 } }}
                >
                    {testChat}
                </Card>
            ) : (
                // Too narrow for both: the test chat opens next to the settings, and stays open while editing.
                <Drawer
                    open={testOpen}
                    onClose={() => setTestOpen(false)}
                    title={t('Test chat')}
                    extra={restart}
                    width={PREVIEW_WIDTH}
                    mask={false}
                    forceRender
                    styles={{ body: { padding: 0 } }}
                >
                    {testChat}
                </Drawer>
            )}
        </div>
    )
}

type SectionProps = {
    values: Values
    onChange: (patch: Partial<Values>) => void
    editable: boolean
}

type SettingProps = {
    label: string
    help?: string
    children: React.ReactNode
}

function Setting({ label, help, children }: SettingProps) {
    return (
        <Flex
            justify="space-between"
            align="center"
            gap="middle"
            wrap
        >
            <Flex
                vertical
                className="min-w-0 flex-1"
            >
                <Typography.Text strong>{label}</Typography.Text>
                {help && <Typography.Text type="secondary">{help}</Typography.Text>}
            </Flex>
            {children}
        </Flex>
    )
}

type AssistantCardProps = SectionProps & {
    assistant: AssistantSettings
}

/** Who the assistant is: its name, its first message, and what it can do. */
function AssistantCard({ values, onChange, editable, assistant }: AssistantCardProps) {
    const { t } = useTranslation()
    const suggestedWelcome = editable ? assistant.voice.profile?.welcome : undefined
    const suggestsWelcome = suggestedWelcome && values.welcomeMessage.trim() !== suggestedWelcome

    return (
        <Card title={t('Assistant')}>
            <Flex
                vertical
                gap="large"
            >
                <Flex
                    vertical
                    gap={4}
                >
                    <Typography.Text strong>{t('Name')}</Typography.Text>
                    <Input
                        value={values.assistantName}
                        maxLength={80}
                        disabled={!editable}
                        placeholder={assistant.defaultAssistantName}
                        onChange={(event) => onChange({ assistantName: event.target.value })}
                    />
                    <Typography.Text type="secondary">{t('Shown at the top of the chat.')}</Typography.Text>
                </Flex>
                <Flex
                    vertical
                    gap={4}
                >
                    <Typography.Text strong>{t('Welcome message')}</Typography.Text>
                    <Input.TextArea
                        value={values.welcomeMessage}
                        rows={3}
                        maxLength={500}
                        disabled={!editable}
                        placeholder={assistant.defaultWelcomeMessage}
                        onChange={(event) => onChange({ welcomeMessage: event.target.value })}
                    />
                    <Typography.Text type="secondary">
                        {t('The first message visitors see when they open the chat.')}
                    </Typography.Text>
                    {suggestsWelcome && (
                        <Typography.Text type="secondary">
                            {t('Suggestion in the tone of your website:')} “{suggestedWelcome}”{' '}
                            <Button
                                type="link"
                                size="small"
                                className="px-0"
                                onClick={() => onChange({ welcomeMessage: suggestedWelcome })}
                            >
                                {t('Use')}
                            </Button>
                        </Typography.Text>
                    )}
                </Flex>
                <CapabilityList
                    values={values}
                    onChange={onChange}
                    editable={editable}
                    capabilities={assistant.capabilities}
                />
            </Flex>
        </Card>
    )
}

type CapabilityLabel = {
    label: string
    help: string
}

type CapabilityListProps = SectionProps & {
    capabilities: AssistantSettings['capabilities']
}

function CapabilityList({ values, onChange, editable, capabilities }: CapabilityListProps) {
    const { t } = useTranslation()

    const capabilityLabels: Record<string, CapabilityLabel> = {
        answer_questions: {
            label: t('Answer questions about the website'),
            help: t('Answers from the pages Evaa read. Always on.'),
        },
        recommend_vacancies: {
            label: t('Recommend vacancies and help people apply'),
            help: t('Shows matching vacancies as cards. Available when the website has vacancies.'),
        },
    }

    const toggle = (key: string, checked: boolean) => {
        const others = values.capabilities.filter((capability) => capability !== key)

        onChange({ capabilities: checked ? [...values.capabilities, key] : others })
    }

    return (
        <Flex
            vertical
            gap="small"
        >
            <Typography.Text strong>{t('What can the assistant do?')}</Typography.Text>
            {/* Answering questions is the base of the assistant: a fact, not a choice. */}
            <Flex
                gap="small"
                align="flex-start"
            >
                <CheckOutlined className="mt-1 text-green-600" />
                <Flex vertical>
                    <span>{capabilityLabels.answer_questions.label}</span>
                    <Typography.Text
                        type="secondary"
                        className="text-xs"
                    >
                        {capabilityLabels.answer_questions.help}
                    </Typography.Text>
                </Flex>
            </Flex>
            {capabilities
                .filter((capability) => capability.key !== 'answer_questions')
                .map((capability) => (
                    <Checkbox
                        key={capability.key}
                        checked={values.capabilities.includes(capability.key)}
                        disabled={!editable || !capability.available}
                        onChange={(event) => toggle(capability.key, event.target.checked)}
                    >
                        <Flex vertical>
                            <span>{capabilityLabels[capability.key]?.label ?? capability.key}</span>
                            <Typography.Text
                                type="secondary"
                                className="text-xs"
                            >
                                {capabilityLabels[capability.key]?.help}
                            </Typography.Text>
                        </Flex>
                    </Checkbox>
                ))}
        </Flex>
    )
}

type WebsiteSectionProps = SectionProps & {
    wordpress: boolean
}

type ChatOnWebsiteCardProps = WebsiteSectionProps & {
    /** The apply window on vacancies is only worth mentioning on a website that has them. */
    hasVacancies: boolean
}

/** Whether the chat shows on the website, the credit link, and the privacy policy it links to. */
function ChatOnWebsiteCard({ values, onChange, editable, wordpress, hasVacancies }: ChatOnWebsiteCardProps) {
    const { t } = useTranslation()

    const privacyHelp = wordpress
        ? t('Comes from WordPress: Settings → Privacy.')
        : t(
              'The chat links to it. Mention the chat there: visitors chat with an AI assistant, and the chats are kept for you.'
          )

    return (
        <Card title={t('Chat on the website')}>
            <Flex
                vertical
                gap="large"
            >
                <Setting
                    label={t('Show the chat on the website')}
                    help={hasVacancies ? t('When it is off, the apply window on vacancies keeps working.') : undefined}
                >
                    <Switch
                        checked={values.enabled}
                        disabled={!editable}
                        onChange={(enabled) => onChange({ enabled })}
                    />
                </Setting>
                <Setting
                    label={t('Show "Powered by Evaa"')}
                    help={t('A small link under the chat. Off unless you turn it on.')}
                >
                    <Switch
                        checked={values.credit === true}
                        disabled={!editable}
                        onChange={(credit) => onChange({ credit })}
                    />
                </Setting>
                <Flex
                    vertical
                    gap={4}
                >
                    <Typography.Text strong>{t('Privacy policy')}</Typography.Text>
                    <Input
                        value={values.privacyUrl ?? ''}
                        disabled={!editable || wordpress}
                        placeholder="https://www.example.com/privacy"
                        onChange={(event) => onChange({ privacyUrl: event.target.value || null })}
                    />
                    <Typography.Text type="secondary">{privacyHelp}</Typography.Text>
                </Flex>
            </Flex>
        </Card>
    )
}

/** How the chat button looks on the website. */
function AppearanceCard({ values, onChange, editable, wordpress }: WebsiteSectionProps) {
    const { t } = useTranslation()

    const automaticColour = wordpress ? t('Uses the main colour of your WordPress theme.') : t('Uses Evaa blue.')

    return (
        <Card title={t('Appearance')}>
            <Flex
                vertical
                gap="large"
            >
                <Setting label={t('Button text')}>
                    <Input
                        className="w-56"
                        value={values.label}
                        maxLength={40}
                        disabled={!editable}
                        placeholder={t('Chat with us')}
                        onChange={(event) => onChange({ label: event.target.value })}
                    />
                </Setting>
                <Setting label={t('Position')}>
                    <Segmented
                        value={values.position}
                        disabled={!editable}
                        onChange={(position) => onChange({ position: position as Values['position'] })}
                        options={[
                            {
                                label: t('Left'),
                                value: 'left',
                            },
                            {
                                label: t('Right'),
                                value: 'right',
                            },
                        ]}
                    />
                </Setting>
                <Setting
                    label={t('Colour')}
                    help={values.color ? undefined : automaticColour}
                >
                    <ColorPicker
                        value={values.color || undefined}
                        allowClear
                        disabledAlpha
                        showText={(color) => (values.color ? color.toHexString() : t('Automatic'))}
                        disabled={!editable}
                        onChange={(color) => onChange({ color: color.cleared ? '' : color.toHexString() })}
                        onClear={() => onChange({ color: '' })}
                    />
                </Setting>
                <Setting
                    label={t('Text colour')}
                    help={values.textColor ? undefined : t('White or dark, whichever reads best.')}
                >
                    <ColorPicker
                        value={values.textColor || undefined}
                        allowClear
                        disabledAlpha
                        showText={(color) => (values.textColor ? color.toHexString() : t('Automatic'))}
                        disabled={!editable}
                        onChange={(color) => onChange({ textColor: color.cleared ? '' : color.toHexString() })}
                        onClear={() => onChange({ textColor: '' })}
                    />
                </Setting>
                <Setting label={t('Rounded corners')}>
                    <Slider
                        className="w-56"
                        min={0}
                        max={24}
                        value={values.radius}
                        disabled={!editable}
                        onChange={(radius) => onChange({ radius })}
                    />
                </Setting>
            </Flex>
        </Card>
    )
}

type SaveBarProps = {
    /** Too narrow for the preview next to the settings: a button opens the test chat. */
    withTestButton: boolean
    dirty: boolean
    saving: boolean
    onTest: () => void
    onCancel: () => void
    onSave: () => void
}

function SaveBar({ withTestButton, dirty, saving, onTest, onCancel, onSave }: SaveBarProps) {
    const { t } = useTranslation()

    return (
        <Flex
            justify="space-between"
            align="center"
            gap="small"
            wrap
            className="sticky bottom-4 z-10 rounded-xl border border-neutral-200 bg-white px-4 py-3 shadow-lg"
        >
            <div>
                {withTestButton && (
                    <Button
                        icon={<MessageOutlined />}
                        onClick={onTest}
                    >
                        {t('Test chat')}
                    </Button>
                )}
            </div>
            <Flex
                align="center"
                gap="small"
            >
                {dirty && <Tag color="orange">{t('Unsaved changes')}</Tag>}
                <Button
                    disabled={!dirty}
                    onClick={onCancel}
                >
                    {t('Cancel')}
                </Button>
                <Button
                    type="primary"
                    disabled={!dirty}
                    loading={saving}
                    onClick={onSave}
                >
                    {t('Save')}
                </Button>
            </Flex>
        </Flex>
    )
}

type TestChatProps = {
    previewRef: React.Ref<ChatPreviewHandle>
    values: Values
    assistant: AssistantSettings
    editable: boolean
    onQuestion?: React.ComponentProps<typeof ChatPreview>['onQuestion']
}

/** The real widget with the settings on the page, which also answers test questions when the user can edit. */
function TestChat({ previewRef, values, assistant, editable, onQuestion }: TestChatProps) {
    const { t, i18n } = useTranslation()
    const suggestions = values.voice.suggestions ?? assistant.voice.profile?.suggestions ?? []

    return (
        <>
            {editable && (
                <Typography.Paragraph
                    type="secondary"
                    className="mb-0 px-4 pt-3"
                >
                    {t(
                        'Chat as a visitor would. The chat uses the settings on the left, also before you save. Test chats are not stored.'
                    )}
                </Typography.Paragraph>
            )}
            <ChatPreview
                ref={previewRef}
                onQuestion={onQuestion}
                className="mt-3 block h-[600px] w-full border-0 bg-neutral-50"
                config={{
                    locale: i18n.language,
                    name: values.assistantName.trim() || assistant.defaultAssistantName,
                    welcome: values.welcomeMessage.trim() || assistant.defaultWelcomeMessage,
                    color: values.color,
                    textColor: values.textColor,
                    position: values.position,
                    radius: values.radius,
                    label: values.label,
                    credit: values.credit === true,
                    privacyUrl: values.privacyUrl ?? '',
                    suggestions: suggestions.filter((question) => question.trim() !== ''),
                }}
            />
        </>
    )
}
